import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Steps,
  Form,
  Input,
  Select,
  Button,
  Space,
  Descriptions,
  Result,
  Spin,
  DatePicker,
  Tabs,
  Tag,
  Checkbox,
  InputNumber,
  Empty,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import * as tmActions from "store/actions/TenantManagementActions";
import PermissionGroupsSelector from "components/Common/PermissionGroupsSelector";

const TenantCreate = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [createdTenant, setCreatedTenant] = useState(null);
  const [error, setError] = useState(null);

  // Onboarding catalog data fetched from the connected ERP via proxy.
  const [plans, setPlans] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // Subscription step state
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [invoiceFetching, setInvoiceFetching] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Role step state
  const [roleMode, setRoleMode] = useState("import"); // "import" | "custom"
  const [pickedTemplateIds, setPickedTemplateIds] = useState([]);
  const [customRolePerms, setCustomRolePerms] = useState(new Set());
  const [adminRoleCode, setAdminRoleCode] = useState("");

  useEffect(() => {
    const fetchConnections = async () => {
      const data = await tmActions.loadAppConnections();
      if (data) {
        const list = Array.isArray(data) ? data : data.results || [];
        setConnections(list.filter((c) => c.is_connected));
      }
    };
    fetchConnections();
  }, []);

  // Fetch ERP onboarding catalog as soon as the user picks an app.
  useEffect(() => {
    if (!selectedConnection) return;
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      const [planList, templateList, groupList] = await Promise.all([
        tmActions.loadOnboardingPlans(selectedConnection.id),
        tmActions.loadOnboardingRoleTemplates(selectedConnection.id),
        tmActions.loadOnboardingPermissionGroups(selectedConnection.id),
      ]);
      if (cancelled) return;
      setPlans(planList || []);
      setRoleTemplates(templateList || []);
      setPermissionGroups(groupList || []);
      // Default-import every is_default template (admin can untick).
      setPickedTemplateIds(
        (templateList || []).filter((t) => t.is_default).map((t) => t.id)
      );
      setCatalogLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedConnection]);

  const planOptions = useMemo(
    () => (plans || []).map((p) => ({ value: p.id, label: `${p.name} — ₹${p.price}`, plan: p })),
    [plans]
  );

  const selectedPlan = useMemo(
    () => (plans || []).find((p) => p.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );

  const fetchInvoiceOptions = async (q = "") => {
    setInvoiceFetching(true);
    const list = await tmActions.searchPaidInvoices(q);
    setInvoiceFetching(false);
    setInvoiceOptions(
      (list || []).map((i) => ({
        value: i.inv_number,
        label: `${i.inv_number} — ${i.account_name || "—"} — ${
          i.total_payable ?? i.total ?? 0
        }`,
        inv: i,
      }))
    );
  };

  const debouncedFetchInvoices = useMemo(() => {
    let timer = null;
    return (q) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fetchInvoiceOptions(q), 300);
    };
  }, []);

  // Preload the invoice dropdown the first time the user reaches the
  // Subscription step. Without this, the dropdown is empty until the
  // user actually types something into the autocomplete.
  useEffect(() => {
    if (currentStep === 2 && invoiceOptions.length === 0 && !invoiceFetching) {
      fetchInvoiceOptions("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const handleNext = async () => {
    if (currentStep === 0) {
      const conn = connections.find((c) => c.id === form.getFieldValue("app_connection"));
      if (!conn) return;
      setSelectedConnection(conn);
      setCurrentStep(1);
    } else if (currentStep === 1) {
      try {
        await form.validateFields(["name", "schema_name", "admin_email"]);
      } catch {
        return; // validation errors shown inline
      }
      // Fail-fast uniqueness check against the remote ERP. Without this
      // a duplicate schema_name only surfaces at the final create step.
      const schemaName = (form.getFieldValue("schema_name") || "").trim();
      const result = await tmActions.checkSchemaNameAvailability(
        selectedConnection.id,
        schemaName,
      );
      if (result && result.available === false) {
        form.setFields([
          {
            name: "schema_name",
            errors: [
              `Schema name "${schemaName}" is already used on ${selectedConnection.product_name}. Pick a different one.`,
            ],
          },
        ]);
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      try {
        await form.validateFields(["plan_id", "amount", "end_date", "invoice_number"]);
        setCurrentStep(3);
      } catch {
        // inline errors
      }
    } else if (currentStep === 3) {
      // Role step: at least one role must be chosen for the admin user.
      const roles = pickedTemplateIds || [];
      const hasCustom = roleMode === "custom" && customRolePerms.size > 0;
      if (roles.length === 0 && !hasCustom) {
        form.setFields([
          {
            name: "_role_step",
            errors: ["Pick at least one role template or build a custom role."],
          },
        ]);
        return;
      }
      if (!adminRoleCode) {
        form.setFields([
          {
            name: "admin_role_code",
            errors: ["Pick which role to assign to the admin user."],
          },
        ]);
        return;
      }
      setCurrentStep(4);
    } else if (currentStep === 4) {
      // Review → submit.
      setCreating(true);
      setError(null);
      setCurrentStep(5);
      try {
        const v = form.getFieldsValue();
        const customRole =
          roleMode === "custom" && customRolePerms.size > 0
            ? {
                name: v.custom_role_name,
                code: (v.custom_role_name || "").toLowerCase().replace(/\s+/g, "_"),
                maxdiscount: v.custom_role_maxdiscount || 0,
                permission_ids: Array.from(customRolePerms),
              }
            : null;

        const payload = {
          tenant: {
            name: v.name,
            schema_name: v.schema_name,
            email: v.admin_email,
            // paid_until is intentionally omitted — the backend sets it
            // from the subscription's end_date so the two stay in sync
            // and the wizard doesn't ask the same question twice.
            on_trial: !!v.on_trial,
          },
          // Domain is intentionally omitted — the backend's
          // create_tenant_with_setup falls back to `<schema>.localhost`
          // when no domain is supplied. The wizard no longer asks for
          // it; the SaaS Product's frontend_url is the source of truth
          // for tenant URLs (set on the Product, not per-tenant).
          domain: "",
          admin_user: {
            email: v.admin_email,
            first_name: v.admin_first_name || "",
            last_name: v.admin_last_name || "",
            password: v.admin_password || "",
          },
          subscription: v.plan_id
            ? {
                plan: v.plan_id,
                amount: Number(v.amount || 0),
                end_date: v.end_date ? v.end_date.toISOString() : null,
                invoice_number: v.invoice_number || "",
              }
            : null,
          roles_to_import: pickedTemplateIds,
          custom_role: customRole,
          admin_role_code: adminRoleCode,
        };

        const result = await tmActions.createTenantWithSetup(
          selectedConnection.id,
          payload
        );
        if (result) setCreatedTenant(result);
        else setError("Failed to create tenant. Please try again.");
      } catch (err) {
        setError(err?.response?.data?.message || err.message || "Failed to create tenant");
      }
      setCreating(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0 && currentStep < 5) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = [
    { title: "Select Product" },
    { title: "Tenant Details" },
    { title: "Subscription" },
    { title: "Role" },
    { title: "Review" },
    { title: "Creating" },
  ];

  const renderStep0 = () => (
    <Form.Item
      name="app_connection"
      label="Select Product"
      rules={[{ required: true, message: "Please select an app" }]}
    >
      <Select
        placeholder="Select a connected app"
        size="large"
        options={connections.map((c) => ({
          value: c.id,
          label: `${c.product_name} (${c.product_code})`,
        }))}
      />
    </Form.Item>
  );

  const renderStep1 = () => (
    <>
      <Form.Item
        name="name"
        label="Tenant Name"
        rules={[{ required: true, message: "Please enter tenant name" }]}
      >
        <Input placeholder="e.g., Rajesh Industries" />
      </Form.Item>
      <Form.Item
        name="schema_name"
        label="Schema Name"
        rules={[
          { required: true, message: "Please enter schema name" },
          { pattern: /^[a-z][a-z0-9_]*$/, message: "Lowercase letters, numbers, and underscores only" },
        ]}
        extra="Used as the PostgreSQL schema name. Must be lowercase."
      >
        <Input placeholder="e.g., rajesh_industries" />
      </Form.Item>
      <Form.Item
        name="admin_email"
        label="Admin Email"
        rules={[
          { required: true, message: "Please enter admin email" },
          { type: "email", message: "Please enter a valid email" },
        ]}
      >
        <Input placeholder="admin@rajesh.com" />
      </Form.Item>
      <Form.Item name="admin_first_name" label="Admin First Name">
        <Input placeholder="Rajesh" />
      </Form.Item>
      <Form.Item name="admin_last_name" label="Admin Last Name">
        <Input placeholder="Kumar" />
      </Form.Item>
      <Form.Item name="admin_password" label="Admin Password">
        <Input.Password placeholder="Initial password for the admin account" />
      </Form.Item>
    </>
  );

  const renderStep2 = () => (
    <>
      <Form.Item
        name="plan_id"
        label="Plan"
        rules={[{ required: true, message: "Pick a plan" }]}
      >
        <Select
          options={planOptions}
          placeholder={catalogLoading ? "Loading plans…" : "Select a plan"}
          loading={catalogLoading}
          onChange={(v) => {
            setSelectedPlanId(v);
            const p = (plans || []).find((pl) => pl.id === v);
            if (p) {
              form.setFieldsValue({ amount: Number(p.price || 0) });
            }
          }}
        />
      </Form.Item>

      {selectedPlan && (
        <div style={{ margin: "0 0 16px 0" }}>
          <strong>Modules included: </strong>
          {selectedPlan.modules?.length ? (
            selectedPlan.modules.map((m) => (
              <Tag key={m.id || m.code}>{m.name || m.code}</Tag>
            ))
          ) : (
            <span style={{ color: "#999" }}>No modules attached to this plan.</span>
          )}
        </div>
      )}

      <Form.Item
        name="amount"
        label="Amount"
        rules={[{ required: true, message: "Enter amount" }]}
      >
        <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
      </Form.Item>

      <Form.Item
        name="end_date"
        label="End Date"
        rules={[{ required: true, message: "Pick subscription end date" }]}
      >
        <DatePicker showTime style={{ width: "100%" }} />
      </Form.Item>

      <Form.Item
        name="invoice_number"
        label="TesonCRM Invoice"
        rules={[{ required: true, message: "Pick a paid invoice" }]}
        extra="Type to search by invoice number or account."
      >
        <Select
          showSearch
          allowClear
          filterOption={false}
          loading={invoiceFetching}
          options={invoiceOptions}
          placeholder="Search by invoice number or account name"
          onSearch={(q) => debouncedFetchInvoices(q)}
          onDropdownVisibleChange={(open) => {
            // Refetch every time the dropdown opens — covers both
            // initial entry and the case where the user opens it via
            // the chevron without focusing the input.
            if (open) fetchInvoiceOptions("");
          }}
          onChange={(val, opt) => setSelectedInvoice(opt?.inv || null)}
          notFoundContent={
            invoiceFetching ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          }
        />
      </Form.Item>
    </>
  );

  const renderStep3 = () => {
    const pickableTemplates = roleTemplates || [];
    const adminRoleOptions = [
      ...pickableTemplates
        .filter((t) => pickedTemplateIds.includes(t.id))
        .map((t) => ({ value: t.code, label: `${t.name} (template)` })),
    ];
    const customCode =
      roleMode === "custom" &&
      (form.getFieldValue("custom_role_name") || "").trim()
        ? (form.getFieldValue("custom_role_name") || "").toLowerCase().replace(/\s+/g, "_")
        : null;
    if (customCode) {
      adminRoleOptions.unshift({ value: customCode, label: `${form.getFieldValue("custom_role_name")} (custom)` });
    }

    return (
      <>
        <Tabs
          activeKey={roleMode}
          onChange={setRoleMode}
          items={[
            {
              key: "import",
              label: "Import templates",
              children: (
                <div>
                  <p style={{ color: "#666", fontSize: 12 }}>
                    Default templates are pre-checked and always cloned. Untick to skip.
                  </p>
                  <Checkbox.Group
                    style={{ display: "grid", gap: 8 }}
                    value={pickedTemplateIds}
                    onChange={(vals) => setPickedTemplateIds(vals)}
                  >
                    {pickableTemplates.map((t) => (
                      <Checkbox key={t.id} value={t.id} disabled={t.is_default}>
                        <strong>{t.name}</strong>{" "}
                        <span style={{ color: "#999" }}>({t.code}) {t.is_default ? "— default" : ""}</span>
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                </div>
              ),
            },
            {
              key: "custom",
              label: "Create custom role",
              children: (
                <div>
                  <Form.Item
                    name="custom_role_name"
                    label="Role Name"
                    rules={[
                      {
                        required: roleMode === "custom",
                        message: "Enter a custom role name",
                      },
                    ]}
                  >
                    <Input placeholder="e.g., Store Supervisor" />
                  </Form.Item>
                  <Form.Item name="custom_role_maxdiscount" label="Maximum Discount (%)">
                    <InputNumber min={0} max={100} step={0.01} style={{ width: 200 }} />
                  </Form.Item>
                  <PermissionGroupsSelector
                    permissionGroups={permissionGroups}
                    value={customRolePerms}
                    onChange={(next) => setCustomRolePerms(new Set(next))}
                    heading="Custom role permissions"
                  />
                </div>
              ),
            },
          ]}
        />

        <Form.Item
          name="admin_role_code"
          label="Role for the admin user"
          rules={[{ required: true, message: "Pick which role gets assigned to the admin user" }]}
          style={{ marginTop: 16 }}
        >
          <Select
            options={adminRoleOptions}
            value={adminRoleCode}
            onChange={(v) => setAdminRoleCode(v)}
            placeholder="Pick the role assigned to the admin user"
            notFoundContent="Pick at least one template or create a custom role first"
          />
        </Form.Item>
      </>
    );
  };

  const renderStep4 = () => {
    const v = form.getFieldsValue();
    const planLabel = (plans || []).find((p) => p.id === v.plan_id);
    const importedNames = (roleTemplates || [])
      .filter((t) => pickedTemplateIds.includes(t.id))
      .map((t) => t.name)
      .join(", ");
    return (
      <Descriptions bordered column={1}>
        <Descriptions.Item label="App">{selectedConnection?.product_name}</Descriptions.Item>
        <Descriptions.Item label="Tenant Name">{v.name}</Descriptions.Item>
        <Descriptions.Item label="Schema Name"><code>{v.schema_name}</code></Descriptions.Item>
        <Descriptions.Item label="Admin Email">{v.admin_email}</Descriptions.Item>
        <Descriptions.Item label="Admin Name">
          {[v.admin_first_name, v.admin_last_name].filter(Boolean).join(" ") || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Plan">{planLabel?.name || "-"}</Descriptions.Item>
        <Descriptions.Item label="Amount">{v.amount ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Subscription End">
          {v.end_date ? v.end_date.format("YYYY-MM-DD HH:mm") : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="TesonCRM Invoice">
          {v.invoice_number ? (
            <a
              href={selectedInvoice ? `/invoices/${selectedInvoice.id}` : "#"}
              target="_blank"
              rel="noreferrer"
            >
              {v.invoice_number}
            </a>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Roles Imported">{importedNames || "(none extra)"}</Descriptions.Item>
        <Descriptions.Item label="Custom Role">
          {roleMode === "custom" && customRolePerms.size > 0
            ? `${v.custom_role_name} (${customRolePerms.size} perms)`
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Admin Role">{adminRoleCode || "-"}</Descriptions.Item>
      </Descriptions>
    );
  };

  const renderStep5 = () => {
    if (creating) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Creating tenant on {selectedConnection?.product_name}...</p>
          <p style={{ color: "#888" }}>Status: pending → setting_up → active</p>
        </div>
      );
    }
    if (error) {
      return (
        <Result
          status="error"
          title="Tenant Creation Failed"
          subTitle={error}
          extra={
            <Button type="primary" onClick={() => { setCurrentStep(4); setError(null); }}>
              Try Again
            </Button>
          }
        />
      );
    }
    return (
      <Result
        status="success"
        title="Tenant Created Successfully"
        subTitle={`${createdTenant?.name || "Tenant"} has been created on ${selectedConnection?.product_name}`}
        extra={[
          // The detail route is keyed on the local SyncedTenant id, not
          // the remote ERP tenant id. The proxy creates that mirror and
          // returns its id as ``synced_tenant_id``; fall back to ``id``
          // only for older deployments that don't yet emit the new key.
          createdTenant?.synced_tenant_id || createdTenant?.id ? (
            <Button
              type="primary"
              key="view"
              onClick={() =>
                navigate(
                  `/tenant-management/tenants/${
                    createdTenant.synced_tenant_id || createdTenant.id
                  }`,
                )
              }
            >
              View Tenant
            </Button>
          ) : null,
          <Button key="list" onClick={() => navigate("/tenant-management/tenants")}>
            Back to List
          </Button>,
        ].filter(Boolean)}
      />
    );
  };

  return (
    <div style={{ padding: "16px" }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/tenant-management/tenants")}>
          Back
        </Button>
        <span style={{ fontSize: 18, fontWeight: 600 }}>Create Tenant</span>
      </Space>

      <Card>
        <Steps current={currentStep} items={steps} style={{ marginBottom: 32 }} />

        <Form form={form} layout="vertical" preserve style={{ maxWidth: 720 }}>
          <div style={{ display: currentStep === 0 ? "block" : "none" }}>{renderStep0()}</div>
          <div style={{ display: currentStep === 1 ? "block" : "none" }}>{renderStep1()}</div>
          <div style={{ display: currentStep === 2 ? "block" : "none" }}>{renderStep2()}</div>
          <div style={{ display: currentStep === 3 ? "block" : "none" }}>{renderStep3()}</div>
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}

          {currentStep < 5 && (
            <Space style={{ marginTop: 24 }}>
              {currentStep > 0 && <Button onClick={handleBack}>Back</Button>}
              <Button type="primary" onClick={handleNext}>
                {currentStep === 4 ? "Create Tenant" : "Next"}
              </Button>
            </Space>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default TenantCreate;
